import { Box, CircularProgress, Stack, Typography } from '@mui/material';
import { DEFAULT_TIMEZONE } from '../../../../constants/appointments';
import useUser from '../../../../hooks/global/useUser';
import { LoadedUser } from '../../../../interfaces';
import useGetCustomerFormsAndFilesTab from '../../../../hooks/customers/useGetCustomerFormsAndFilesTab';
import useWaiverRequestsTableActions from '../../../../hooks/waivers/useWaiverRequestsTableActions';
import FormsTable from './FormsTable';
import FilesList from './FilesList';

interface Props {
  customerId: string;
  businessId: string;
  onUploadFile: () => void;
}

function FormsAndFilesTab({ customerId, businessId, onUploadFile }: Props) {
  const { user } = useUser<LoadedUser>();
  const {
    waiverRequests,
    files,
    isInitialLoading,
    isFetchingFiles,
    hasMoreFiles,
    loadMoreFiles,
    filesLoadMoreError,
    isError,
  } = useGetCustomerFormsAndFilesTab({ customerId, businessId });
  const { sendReminder, viewWaiver } = useWaiverRequestsTableActions();

  if (isInitialLoading) {
    return (
      <Stack
        alignItems="center"
        justifyContent="center"
        sx={{ height: '200px' }}>
        <CircularProgress color="primary" />
      </Stack>
    );
  }

  if (isError) {
    return (
      <Typography variant="body1" sx={{ color: 'text.secondary' }}>
        There was an error loading this customer&apos;s forms and files.
      </Typography>
    );
  }

  const timezone = user?.timezone ?? DEFAULT_TIMEZONE;

  return (
    <Box>
      <FormsTable
        waiverRequests={waiverRequests}
        timezone={timezone}
        onView={viewWaiver}
        onResend={sendReminder}
      />
      <FilesList
        files={files}
        timezone={timezone}
        onUploadFile={onUploadFile}
        hasMore={hasMoreFiles}
        isLoadingMore={isFetchingFiles}
        loadMoreError={filesLoadMoreError}
        onLoadMore={loadMoreFiles}
      />
    </Box>
  );
}

export default FormsAndFilesTab;
